import { AuditLogEmptyState } from './audit-log-empty-state';
import { AuditTrailTable } from './audit-trail-table';
import { useFetchOrgMatters } from '@/api/queries/analyticsQueries/useFetchOrgMatters';
import { useFetchOrgUsers } from '@/api/queries/analyticsQueries/useFetchOrgUsers';
import { User } from '@/api/types/backend-dto-types';
import { SettingsSectionHeading } from '@/new/components/settings-section';
import { Empty, EmptyDescription, EmptyHeader, EmptyTitle } from '@/new/components/shadcn/empty';
import { cn } from '@/new/lib/utils';

// Audit tab — org-wide audit log with user / matter filters in its popover.
export function AuditTab({
  organisationId,
  isViewingOtherOrg,
  settingsLayout = false,
}: {
  organisationId: string;
  isViewingOtherOrg: boolean;
  settingsLayout?: boolean;
}) {
  const { data: usersData } = useFetchOrgUsers(organisationId);
  const { data: mattersData } = useFetchOrgMatters(organisationId);

  const userOptions = (usersData ?? []).map((u: User) => ({ label: u.email, value: u.id }));
  const matterOptions = (mattersData ?? []).map((m) => ({
    label: isViewingOtherOrg ? m.id : m.name,
    value: m.id,
  }));

  return (
    <div className={cn(settingsLayout && 'flex min-h-0 flex-1 flex-col')}>
      {settingsLayout ? (
        <SettingsSectionHeading
          title="Audit log"
          description="A detailed, filterable record of every action across the organisation."
          inset={false}
          className="mb-6 px-8"
        />
      ) : (
        <div className="my-6 ml-2">
          <h2 className="text-lg font-semibold text-gray-900">Audit Log</h2>
          <p className="mt-0.5 text-sm text-gray-500">
            A detailed, filterable record of every action across the organisation.
          </p>
        </div>
      )}

      {isViewingOtherOrg ? (
        <Empty className="py-14">
          <EmptyHeader>
            <AuditLogEmptyState />
            <EmptyTitle>Audit log unavailable</EmptyTitle>
            <EmptyDescription>The audit log isn't available when viewing another organisation.</EmptyDescription>
          </EmptyHeader>
        </Empty>
      ) : (
        <div className={cn(settingsLayout ? 'flex min-h-0 flex-1 flex-col' : 'mx-2 mb-10 w-full')}>
          <AuditTrailTable
            key={organisationId}
            grammar="a"
            organisationId={organisationId}
            userOptions={userOptions}
            matterOptions={matterOptions}
            className={cn(settingsLayout && 'overflow-hidden px-8')}
          />
        </div>
      )}
    </div>
  );
}
